import { Actions, createEffect, ofType } from "@ngrx/effects";
import {
  AuthenticationActions,
  InstructionDetailsActions,
  NotificationActions,
  SettingsActions,
} from "../actions";
import { catchError, map, mergeMap, tap } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { of } from "rxjs";
import { SettingsService } from "src/app/services/settings.service";
import { TranslateService } from "@ngx-translate/core";
import { Settings } from "luxon";

@Injectable()
export class SettingsEffects {
  changePassword$ = createEffect(() =>
    this.actions.pipe(
      ofType(SettingsActions.changePassword),
      mergeMap((action) =>
        this.settingsService
          .changePassword(action.oldPassword, action.newPassword)
          .pipe(
            map(() => SettingsActions.changedPassword()),
            catchError((error) =>
              of(SettingsActions.changePasswordFailed(error)),
            ),
          ),
      ),
    ),
  );

  resetPassword$ = createEffect(() =>
    this.actions.pipe(
      ofType(SettingsActions.resetPassword),
      mergeMap((action) =>
        this.settingsService
          .setNewPassword(action.key, action.newPassword)
          .pipe(
            map(() => SettingsActions.resetPasswordSuccessfully()),
            catchError((error) =>
              of(SettingsActions.resetPasswordFailed(error)),
            ),
          ),
      ),
    ),
  );

  triggerSuccessNotificationsForPassword$ = createEffect(() =>
    this.actions.pipe(
      ofType(
        SettingsActions.changedPassword,
        SettingsActions.resetPasswordSuccessfully,
      ),
      map((_) =>
        NotificationActions.notify({
          notification: {
            durationMs: 5000,
            notificationType: "success",
            notification: this.translate.instant(
              "settings.change-password.success",
            ),
          },
        }),
      ),
    ),
  );

  triggerLogoutAfterSuccessfulPasswordChange$ = createEffect(() =>
    this.actions.pipe(
      ofType(
        SettingsActions.changedPassword,
        SettingsActions.resetPasswordSuccessfully,
      ),
      map((_) => AuthenticationActions.logout()),
    ),
  );

  triggerFailureNotificationsForPassword$ = createEffect(() =>
    this.actions.pipe(
      ofType(
        SettingsActions.changePasswordFailed,
        SettingsActions.resetPasswordFailed,
      ),
      map((_) =>
        NotificationActions.notify({
          notification: {
            durationMs: 5000,
            notificationType: "error",
            notification: this.translate.instant(
              "settings.change-password.failure",
            ),
          },
        }),
      ),
    ),
  );

  changeLanguage$ = createEffect(() =>
    this.actions.pipe(
      ofType(SettingsActions.changeLanguage),
      mergeMap((action) =>
        this.settingsService.changeLanguage(action.language).pipe(
          map((_) =>
            SettingsActions.changedLanguage({ language: action.language }),
          ),
          catchError((error) =>
            of(SettingsActions.changeLanguageFailed({ error })),
          ),
        ),
      ),
    ),
  );

  triggerSuccessNotifications$ = createEffect(() =>
    this.actions.pipe(
      ofType(SettingsActions.changedLanguage),
      map((_) =>
        NotificationActions.notify({
          notification: {
            durationMs: 5000,
            notificationType: "success",
            notification: this.translate.instant("common.success"),
          },
        }),
      ),
    ),
  );

  // triggerReloadAfterPasswordChange$ = createEffect(
  //   () =>
  //     this.actions.pipe(
  //       ofType(SettingsActions.changedLanguage),
  //       tap((_) => window.location.reload()),
  //     ),
  //   { dispatch: false },
  // );

  triggerFailureNotifications$ = createEffect(() =>
    this.actions.pipe(
      ofType(SettingsActions.changeLanguageFailed),
      map((_) =>
        NotificationActions.notify({
          notification: {
            durationMs: 5000,
            notificationType: "error",
            notification: this.translate.instant("common.failure"),
          },
        }),
      ),
    ),
  );

  constructor(
    private readonly actions: Actions,
    private readonly settingsService: SettingsService,
    private readonly translate: TranslateService,
  ) {}
}
